import React, { useEffect, useState } from "react";
import { Upload, Trash2, Loader2, MapPin } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#00E5FF]";

export default function ImageVault() {
  const [images, setImages] = useState([]);
  const [file, setFile] = useState(null);
  const [locationTag, setLocationTag] = useState("");
  const [altText, setAltText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/images").then((r) => setImages(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an image first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("location_tag", locationTag);
    fd.append("alt_text", altText);
    try {
      await api.post("/admin/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to the image vault");
      setFile(null); setLocationTag(""); setAltText(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/images/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-5">Image Vault</h2>
      <p className="text-neutral-500 text-sm mb-6 max-w-2xl">Upload real, location-tagged photos of RiNo venues. Adding accurate location tags and alt text helps Google rank the district's listings.</p>

      <form onSubmit={upload} className="border border-white/10 bg-[#141414] p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="sm:col-span-2">
          <label className="text-neutral-400 text-xs">Image file</label>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} data-testid="vault-file" className="w-full mt-1 text-sm text-neutral-300 file:mr-3 file:border-0 file:bg-[#FF0055] file:text-white file:px-3 file:py-2 file:text-sm" />
        </div>
        <div>
          <label className="text-neutral-400 text-xs">Location tag</label>
          <input value={locationTag} onChange={(e) => setLocationTag(e.target.value)} placeholder="e.g. Ironworks Brewing, 2500 Larimer St" data-testid="vault-location" className={`mt-1 ${inputCls}`} />
        </div>
        <div>
          <label className="text-neutral-400 text-xs">Alt text</label>
          <input value={altText} onChange={(e) => setAltText(e.target.value)} placeholder="Describe the photo" data-testid="vault-alt" className={`mt-1 ${inputCls}`} />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={busy} data-testid="vault-upload-btn" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? "Uploading..." : "Upload to Vault"}
          </Button>
        </div>
      </form>

      <h3 className="font-accent text-[#00E5FF] text-sm mb-3">Vault ({images.length})</h3>
      {images.length === 0 ? (
        <div className="text-neutral-500 text-sm py-10 text-center border border-white/10">No images yet. Upload the first real RiNo photo.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((im) => (
            <div key={im.id} className="border border-white/10 bg-[#141414] group" data-testid={`vault-image-${im.id}`}>
              <div className="relative h-40 overflow-hidden">
                <img src={`${API}/images/${im.id}/file`} alt={im.alt_text} className="w-full h-full object-cover" />
                <button onClick={() => del(im.id)} data-testid={`vault-delete-${im.id}`} className="absolute top-2 right-2 bg-black/70 text-white p-1.5 opacity-0 group-hover:opacity-100 hover:text-[#FF0055] transition-opacity"><Trash2 size={14} /></button>
              </div>
              <div className="p-3">
                <div className="text-neutral-300 text-xs flex items-start gap-1"><MapPin size={12} className="text-[#FF0055] mt-0.5" /> {im.location_tag || "No location"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
